"use client";

// Web Speech API wrapper.
//
// What it provides:
//   - voices: SpeechSynthesisVoice[] — the user's installed voices,
//     refreshed when the browser fires `voiceschanged` (which happens
//     asynchronously on Chrome/Edge after first call).
//   - speak(text, opts) — fire a single utterance, honouring the
//     selected voice + volume.
//   - pickDefaultVoice(voices) — best-effort match for a Cortana-style
//     voice (Microsoft Aria > Jenny > en-US female > system default).
//   - unlocked / requestUnlock — autoplay-unlock surface. Browsers
//     vary: Chrome usually lets the first speech go through after any
//     user click anywhere; Safari is stricter. We expose the state so
//     the settings UI can surface a banner when needed.
//
// All API access is guarded against SSR (this hook is "use client"
// but Next 16 still server-renders client components for the initial
// HTML, where `window.speechSynthesis` is undefined).

import { useCallback, useEffect, useRef, useState } from "react";

const VOICE_PREFERENCE_ORDER: RegExp[] = [
  // Microsoft neural family — modern Cortana-adjacent voices.
  /Microsoft Aria/i,
  /Microsoft Jenny/i,
  /Microsoft Davis/i,
  // Generic "online (Natural)" Microsoft neural voices.
  /Microsoft .* (Online|Natural)/i,
  // Apple's female en-US default.
  /Samantha/i,
];

export function pickDefaultVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  if (voices.length === 0) return null;
  for (const pattern of VOICE_PREFERENCE_ORDER) {
    const m = voices.find((v) => pattern.test(v.name));
    if (m) return m;
  }
  // Last resort: any en-US voice, otherwise the very first one.
  const enUs = voices.find((v) => v.lang.toLowerCase().startsWith("en-us"));
  return enUs ?? voices[0] ?? null;
}

export interface SpeakOptions {
  voiceURI?: string | null;
  volume?: number;
  rate?: number;
  pitch?: number;
}

export interface UseSpeechSynthesisReturn {
  /** Whether window.speechSynthesis is available at all. */
  supported: boolean;
  /** Installed voices, refreshed on `voiceschanged`. */
  voices: SpeechSynthesisVoice[];
  /** Speak a phrase. Returns true if the utterance was queued. */
  speak: (text: string, opts?: SpeakOptions) => boolean;
  /**
   * Best guess at whether the browser has accepted audio playback for
   * this tab. Starts true on browsers that don't gate Web Speech (most
   * Chromium); flips to false on observed failures and back to true
   * after a successful utterance.
   */
  unlocked: boolean;
}

export function useSpeechSynthesis(): UseSpeechSynthesisReturn {
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [unlocked, setUnlocked] = useState<boolean>(true);
  const supportedRef = useRef<boolean>(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    supportedRef.current = true;
    const synth = window.speechSynthesis;

    const refresh = () => {
      // getVoices can return [] on first call; the browser fires
      // `voiceschanged` shortly after with the populated list.
      setVoices(synth.getVoices());
    };
    refresh();
    synth.addEventListener("voiceschanged", refresh);
    return () => {
      synth.removeEventListener("voiceschanged", refresh);
    };
  }, []);

  const speak = useCallback<UseSpeechSynthesisReturn["speak"]>((text, opts) => {
    if (typeof window === "undefined" || !window.speechSynthesis) return false;
    if (!text.trim()) return false;

    const synth = window.speechSynthesis;
    const utter = new SpeechSynthesisUtterance(text);

    if (opts?.voiceURI) {
      const v = synth.getVoices().find((vv) => vv.voiceURI === opts.voiceURI);
      if (v) utter.voice = v;
    }
    if (typeof opts?.volume === "number") utter.volume = Math.max(0, Math.min(1, opts.volume));
    if (typeof opts?.rate === "number") utter.rate = opts.rate;
    if (typeof opts?.pitch === "number") utter.pitch = opts.pitch;

    utter.onstart = () => setUnlocked(true);
    utter.onerror = (e) => {
      // Safari fires error "not-allowed" before any user gesture has
      // unlocked playback. Surface so the UI can prompt.
      if ((e as SpeechSynthesisErrorEvent).error === "not-allowed") {
        setUnlocked(false);
      }
    };

    synth.speak(utter);
    return true;
  }, []);

  return {
    supported: supportedRef.current,
    voices,
    speak,
    unlocked,
  };
}
